import { defineCustomElement, getAttribute, getBooleanAttribute, getReactEventHandler, isAttrTrue, NectaryElement, updateAttribute, updateBooleanAttribute, updateExplicitBooleanAttribute } from '../utils';
const templateHTML = '<style>:host{display:inline-block;vertical-align:middle;outline:0}button{all:initial;position:relative;display:flex;flex-direction:column;justify-content:space-between;box-sizing:border-box;width:var(--sinch-local-size);height:var(--sinch-local-size);padding:16px 8px 8px;border-radius:var(--sinch-local-shape-radius);background-color:var(--sinch-local-color-background);box-shadow:var(--sinch-local-shadow);cursor:pointer;--sinch-local-color-background:var(--sinch-comp-tile-control-color-default-background-initial);--sinch-local-color-border:var(--sinch-comp-tile-control-color-default-border-initial);--sinch-local-color-text:var(--sinch-comp-tile-control-color-default-text-initial);--sinch-local-color-icon:var(--sinch-comp-tile-control-color-default-icon-initial);--sinch-local-shadow:var(--sinch-comp-tile-control-shadow-initial);--sinch-local-shape-radius:var(--sinch-comp-tile-control-shape-radius-l);--sinch-local-size:88px;--sinch-local-size-icon:var(--sinch-comp-tile-control-size-m-icon)}button::before{content:"";position:absolute;inset:0;border:1px solid var(--sinch-local-color-border);border-radius:var(--sinch-local-shape-radius);pointer-events:none}button:focus-visible::after{position:absolute;content:"";inset:-4px;padding:2px;border:2px solid var(--sinch-comp-tile-control-color-default-outline-focus);border-radius:calc(var(--sinch-local-shape-radius) + 4px);pointer-events:none}:host([data-small])>button{padding:12px 4px 4px;--sinch-local-shape-radius:var(--sinch-comp-tile-control-shape-radius-s);--sinch-local-size:64px;--sinch-local-size-icon:var(--sinch-comp-tile-control-size-s-icon)}button:enabled:hover{--sinch-local-color-background:var(--sinch-comp-tile-control-color-default-background-hover);--sinch-local-color-border:var(--sinch-comp-tile-control-color-default-border-hover);--sinch-local-shadow:var(--sinch-comp-tile-control-shadow-hover)}:host([data-checked])>button:enabled,button:enabled:active{--sinch-local-color-background:var(--sinch-comp-tile-control-color-checked-background-initial);--sinch-local-color-border:var(--sinch-comp-tile-control-color-checked-border-initial);--sinch-local-color-text:var(--sinch-comp-tile-control-color-checked-text-initial);--sinch-local-color-icon:var(--sinch-comp-tile-control-color-checked-icon-initial);--sinch-local-shadow:var(--sinch-comp-tile-control-shadow-active)}:host([data-checked])>button:enabled::before,button:enabled:active::before{border-width:2px}button:disabled{cursor:initial;--sinch-local-color-background:var(--sinch-comp-tile-control-color-disabled-background-initial);--sinch-local-color-border:var(--sinch-comp-tile-control-color-disabled-border-initial);--sinch-local-color-text:var(--sinch-comp-tile-control-color-disabled-text-initial);--sinch-local-color-icon:var(--sinch-comp-tile-control-color-disabled-icon-initial);--sinch-local-shadow:var(--sinch-comp-tile-control-shadow-disabled)}#text{display:inline-block;min-height:20px;text-align:center;font:var(--sinch-comp-tile-control-font-label);color:var(--sinch-local-color-text);overflow:hidden;white-space:nowrap;text-overflow:ellipsis}#icon{width:var(--sinch-local-size-icon);height:var(--sinch-local-size-icon);align-self:center;--sinch-global-size-icon:var(--sinch-local-size-icon);--sinch-global-color-icon:var(--sinch-local-color-icon)}</style><button><div id="icon"><slot name="icon"></slot></div><span id="text"></span></button>';
const template = document.createElement('template');
template.innerHTML = templateHTML;
defineCustomElement('sinch-tile-control-option', class extends NectaryElement {
  #$button;
  #$text;
  constructor() {
    super();
    const shadowRoot = this.attachShadow();
    shadowRoot.appendChild(template.content.cloneNode(true));
    this.#$button = shadowRoot.querySelector('button');
    this.#$text = shadowRoot.querySelector('#text');
  }
  connectedCallback() {
    this.setAttribute('role', 'tab');
    this.#$button.addEventListener('click', this.#onButtonClick);
    this.#$button.addEventListener('focus', this.#onButtonFocus);
    this.#$button.addEventListener('blur', this.#onButtonBlur);
    this.addEventListener('-focus', this.#onFocusReactHandler);
    this.addEventListener('-blur', this.#onBlurReactHandler);
  }
  disconnectedCallback() {
    this.#$button.removeEventListener('click', this.#onButtonClick);
    this.#$button.removeEventListener('focus', this.#onButtonFocus);
    this.#$button.removeEventListener('blur', this.#onButtonBlur);
    this.removeEventListener('-focus', this.#onFocusReactHandler);
    this.removeEventListener('-blur', this.#onBlurReactHandler);
  }
  static get observedAttributes() {
    return ['data-checked', 'text', 'disabled'];
  }
  attributeChangedCallback(name, oldVal, newVal) {
    switch (name) {
      case 'text':
        {
          this.#$text.textContent = newVal;
          break;
        }
      case 'data-checked':
        {
          updateExplicitBooleanAttribute(this, 'aria-selected', isAttrTrue(newVal));
          break;
        }
      case 'disabled':
        {
          this.#$button.disabled = isAttrTrue(newVal);
          break;
        }
    }
  }
  set value(value) {
    updateAttribute(this, 'value', value);
  }
  get value() {
    return getAttribute(this, 'value', '');
  }
  set disabled(isDisabled) {
    updateBooleanAttribute(this, 'disabled', isDisabled);
  }
  get disabled() {
    return getBooleanAttribute(this, 'disabled');
  }
  set text(value) {
    updateAttribute(this, 'text', value);
  }
  get text() {
    return getAttribute(this, 'text', '');
  }
  get focusable() {
    return true;
  }
  focus() {
    this.#$button.focus();
  }
  blur() {
    this.#$button.blur();
  }
  #onButtonClick = e => {
    e.stopPropagation();
    this.dispatchEvent(new CustomEvent('option-change', {
      detail: this.value,
      bubbles: true
    }));
  };
  #onButtonFocus = () => {
    this.dispatchEvent(new CustomEvent('-focus'));
  };
  #onButtonBlur = () => {
    this.dispatchEvent(new CustomEvent('-blur'));
  };
  #onFocusReactHandler = () => {
    getReactEventHandler(this, 'on-focus')?.();
  };
  #onBlurReactHandler = () => {
    getReactEventHandler(this, 'on-blur')?.();
  };
});